// src/components/RecepcionModal.js
import React from "react";
import DataTable from "react-data-table-component";

const RecepcionModal = ({ order, onClose }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const s = String(dateStr).trim();
        // Caso 1: fecha tipo YYYY-MM-DD (tratarla como literal, sin zona horaria)
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
            const [, y, mm, dd] = m;
            return `${dd}/${mm}/${y}`;
        }
        // Caso 2: otro formato con tiempo; usar getters UTC para evitar corrimiento por zona horaria
        const d = new Date(s);
        if (isNaN(d)) return s;
        const day = String(d.getUTCDate()).padStart(2, "0");
        const month = String(d.getUTCMonth() + 1).padStart(2, "0");
        const year = d.getUTCFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatHora = (horaEntera) => {
        if (!horaEntera) return "";
        const str = horaEntera.toString().padStart(4, '0');
        return `${str.slice(0, 2)}:${str.slice(2)}`;
    };

    // Agrupar líneas por ItemCode y LineNum
    const groupedLines = Object.values(order.Orders.reduce((acc, line) => {
        const key = `${line.ItemCode}_${line.LineNum}`;
        if (!acc[key]) {
            acc[key] = {
                ItemCode: line.ItemCode,
                Dscription: line.Dscription,
                // Fecha planificada (por si no hay remisiones)
                ShipDate: line.ShipDate,
                Quantity: line.Quantity,
                OpenQty: line.OpenQty,
                Remisiones: []
            };
        }

    // Usar únicamente los campos reales provistos por el backend, sin reemplazos
    const DocNumRem = line.DocNumRem;
    const NroRemision = line.NroRemision;
    const FechaRem = line.FechaRem;
    const DocTime = line.DocTime;
    const CantidadRecibida = line.CantidadRecibida;

    const hasDoc = DocNumRem != null && String(DocNumRem).trim() !== "";
    const sameAsOC = hasDoc && String(DocNumRem).trim() === String(order.DocNum).trim();
    const hasRemisionNumber = NroRemision != null && String(NroRemision).trim() !== "";
    const hasFecha = FechaRem != null && String(FechaRem).trim() !== "";
    const hasHora = DocTime != null && String(DocTime).trim() !== "";
    const hasQty = parseFloat(CantidadRecibida || 0) > 0;
    // Mostrar remisión solo si tiene algún dato real; si solo hay DocNumRem igual a la OC y nada más, ocultar
    const hasRem = hasRemisionNumber || hasQty || hasFecha || hasHora || (hasDoc && !sameAsOC);

        if (hasRem) {
            acc[key].Remisiones.push({
                DocNumRem,
                NroRemision,
                FechaRem,
                DocTime,
                CantidadRecibida
            });
        }

        return acc;
    }, {}));

    const parseDateToUTC = (s) => {
        if (!s) return null;
        const str = String(s).trim();
        const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
            const [, y, mm, dd] = m;
            return Date.UTC(Number(y), Number(mm) - 1, Number(dd));
        }
        const d = new Date(str);
        if (isNaN(d)) return null;
        return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    };

    const getFechaRecepcion = (row) => {
        const fechas = (row.Remisiones || []).map(r => r.FechaRem).filter(Boolean);
        if (fechas.length > 0) {
            // Tomar la más temprana
            const minTs = fechas.reduce((min, cur) => {
                const ts = parseDateToUTC(cur);
                return ts === null ? min : (min === null ? ts : Math.min(min, ts));
            }, null);
            if (minTs !== null) {
                const d = new Date(minTs);
                return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
            }
        }
        // Fallback: ShipDate
        return formatDate(row.ShipDate);
    };

    const columns = [
        {
            name: "Descripción",
            selector: row => row.Dscription,
            sortable: true,
            wrap: true,
            grow: 2
        },
        {
            name: "Fecha Recepción",
            selector: row => getFechaRecepcion(row),
            sortable: true
        },
        {
            name: "Cantidad Pedida",
            selector: row => row.Quantity,
            sortable: true
        },
        {
            name: "Cantidad Pendiente",
            selector: row => row.OpenQty,
            sortable: true
        }
    ];

    const ExpandedComponent = ({ data }) => {
        if (!data.Remisiones || data.Remisiones.length === 0) return null;
        return (
            <div className="p-2">
                <h6 className="mb-2">Remisiones recibidas</h6>
                <table className="table table-sm table-bordered" style={{ fontSize: "12px" }}>
                    <thead>
                        <tr>
                            <th>Nro Documento</th>
                            <th>Nro Remisión</th>
                            <th>Fecha</th>
                            <th>Hora</th>
                            <th>Cantidad Recibida</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.Remisiones.map((rem, idx) => (
                            <tr key={idx}>
                                <td>{rem.DocNumRem}</td>
                                <td>{rem.NroRemision}</td>
                                <td>{formatDate(rem.FechaRem)}</td>
                                <td>{formatHora(rem.DocTime)}</td>
                                <td>{rem.CantidadRecibida}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-scrollable modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Detalle de Orden #{order.DocNum}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <div className="mb-3">
                            <div><strong>Proveedor:</strong> {order.CardName}</div>
                            <div><strong>Fecha OC:</strong> {formatDate(order.DocDate)}</div>
                            <div><strong>Comentario:</strong> {order.Comments}</div>
                            <div><strong>Proyecto:</strong> {order.Project || "N/A"}</div>
                        </div>

                        <DataTable
                            columns={columns}
                            data={groupedLines}
                            expandableRows
                            expandableRowsComponent={ExpandedComponent}
                            expandableRowDisabled={(row) => !row.Remisiones || row.Remisiones.length === 0}
                            dense
                            pagination
                        />
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecepcionModal;
